import { useMemo } from 'react';

import { Agreement } from '@/shared/api/borrow';

type UseGetBorrowRateProps = {
    agreement: Agreement;
    collateralStressTested: string | null;
    borrowAmount: number;
    prices: string | null;
    collateralMarketValue: string | null;
};

export const useGetBorrowRate = ({
    agreement,
    collateralStressTested,
    borrowAmount,
    prices,
    collateralMarketValue,
}: UseGetBorrowRateProps) => {
    const LTV = useMemo(() => {
        if (!agreement || !borrowAmount || !prices) {
            return null;
        }

        const result =
            (Number(borrowAmount) / (Number(collateralMarketValue) + Number(borrowAmount))) * 100;

        return result.toFixed(2);
    }, [agreement, borrowAmount, prices, collateralMarketValue]);

    const MAX_LTV = useMemo(() => {
        if (!collateralStressTested || !agreement || !borrowAmount) {
            return null;
        }

        // stress tested value without leverage
        const STWL = Number(collateralStressTested);

        const liquidationRiskFactor = Number(agreement.liquidationThreshold);

        const liquidationFeePercent = (100 - Number(agreement?.liquidationFee)) / 100;
        const liquidationRiskFactorPercent = Number(
            liquidationRiskFactor - liquidationFeePercent,
        ).toFixed(4);

        const liquidationThresholdValue = Number(STWL) * Number(liquidationFeePercent);

        const liquidationLeverage =
            Number(liquidationThresholdValue) / Number(liquidationRiskFactorPercent);

        const maxLLTV =
            (Number(liquidationLeverage) / (Number(liquidationLeverage) + Number(STWL))) * 100;

        return Number(maxLLTV).toFixed(2);
    }, [LTV, collateralStressTested, agreement, borrowAmount]);

    return {
        LTV,
        MAX_LTV,
    };
};
